import React, { FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link, useHistory, useLocation } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { StickyColumnWithEntityStatus } from "go-core/components/StickyColumnWithEntityStatus";
import { useWindowSize } from "go-core/components/useWindowSize";
import RenderColumnUtils from "go-list/core/components/Actions/services/RenderColumnUtils";
import { FilterCondition } from "go-list/core/components/Filter/services/types";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { GoListSegmentType, ListConfig } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { ClientGroupApi, ClientGroupDiscountApi } from "../../../../../../../../../../services/Api/Organization/types";
import { convertListStatusToEntityStatus } from "../../../../../../../../../../utils/entityStatus/entityStatus";

interface ListState {
	resource?: Record<string, any>;
	mobileActions: MobileActionProps[];
}

const listName = "CLIENT_GROUP";
const resourceType = "CLIENT_GROUP";

const List: FC<ListState> = ({ resource, mobileActions }) => {
	const history = useHistory();
	const location = useLocation();
	const { t } = useTranslation();
	const { addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const segmentContext = useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const restoreClientGroup = async (clientGroup: ClientGroupApi) => {
		await apiOrganization.restoreClientGroup(clientGroup.id);
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const removeClientGroup = async (clientGroup: ClientGroupApi) => {
		await apiOrganization.removeClientGroup(clientGroup.id);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	let config = {
		fields: [
			{
				id: "name",
				name: t("common.word.name", { ns: "lib" }),
				type: "text",
				render: (item: ClientGroupApi) => {
					return (
						<StickyColumnWithEntityStatus
							status={convertListStatusToEntityStatus(item.status)}
							minWidth={200}
							to={`${location.pathname}/${item.id}`}
						>
							{item.name}
						</StickyColumnWithEntityStatus>
					);
				},
				renderExport: (item: ClientGroupApi) => item.name,
			},
			{
				id: "discount",
				name: t("common.word.discounts"),
				render: (item: ClientGroupApi) => {
					return item.discounts.map((discount: ClientGroupDiscountApi, index: number) => {
						return (
							<React.Fragment key={discount.id}>
								<Link to={`/${organization.id}/menu/discounts/${discount.id}`}>{discount.name}</Link>
								{index !== item.discounts.length - 1 && ", "}
							</React.Fragment>
						);
					});
				},
				renderExport: (item: ClientGroupApi) => {
					return item.discounts.map((x) => x.name).join(", ");
				},
				disableSorting: true,
			},
			{
				id: "daily_discount_limit",
				name: t("modules.client_groups.field.daily_discount_limit.title"),
				type: "number",
				render: (item: ClientGroupApi) => {
					return FormatMoney(item.daily_discount_limit);
				},
				renderExport: (item: ClientGroupApi) => {
					return FormatMoney(item.daily_discount_limit);
				},
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
			{ id: "reference_id", name: t("common.word.reference", { ns: "lib" }), type: "text" },
		],
		actions: [
			{
				name: `${t("common.action.edit", { ns: "lib" })}`,
				link: (item: ClientGroupApi) => {
					return `${location.pathname}/${item.id}`;
				},
			},
			{
				name: `${t("common.action.remove", { ns: "lib" })}`,
				click: (item: ClientGroupApi) => removeClientGroup(item),
				visible: (item: ClientGroupApi) => {
					return item.status === "ENABLED";
				},
				refreshListAfter: true,
				confirmation: {
					use: true,
					message: t("confirmation.message.remove", { ns: "lib" }),
				},
			},
			{
				name: `${t("common.action.activate", { ns: "lib" })}`,
				click: (item: ClientGroupApi) => restoreClientGroup(item),
				visible: (item: ClientGroupApi) => {
					return item.status === "DELETED";
				},
				refreshListAfter: true,
				confirmation: {
					use: true,
					message: t("confirmation.message.activate", { ns: "lib" }),
				},
			},
		],
		selectedColumns: ["name", "discount"],
		filters: [
			{
				id: "status",
				name: t("common.word.status", { ns: "lib" }),
				type: "list",
				options: {
					ENABLED: t("enums.common.status.ENABLED", { ns: "lib" }),
					DELETED: t("enums.common.status.DELETED", { ns: "lib" }),
				},
			},
			{
				id: "discount",
				name: t("common.word.discount"),
				type: "search_select",
				source: {
					request: (search: string, filterParams: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getListDiscountsSearchSelect(search, filterParams, {
							cancelToken: options?.token,
						}),
				},
				disableSorting: true,
			},
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
			{
				id: "deleted",
				name: t("go_list.filters.deleted", { ns: "lib" }),
				slug: "deleted",
				filters: [
					{
						filterId: "status",
						value: "DELETED",
						condition: "e" as FilterCondition,
					},
				],
			} as GoListSegmentType,
		],
		exportConfig: {
			title: t("modules.client_groups.field.export_config.title"),
			filename: t("modules.client_groups.header.title"),
			organization: `${organization.name}`,
			taxIdNo: organization?.more?.print_company_on_pdf ? organization.more?.company_tax_id_no : undefined,
			company: organization?.more?.print_company_on_pdf ? organization.more?.company_name : undefined,
			pdfOrientation: organization?.more?.pdf_orientation,
			pdfFontSize: organization?.more?.default_pdf_font_size?.toString(),
		},
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "discounts,client_group";
			return apiOrganization.getClientGroups(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		doesIdColumnRedirectToPreviewPage: true,
		numberOfStickyColumnsAtTheStart: 1,
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};

	return (
		<ListData
			config={config}
			emptyList={{
				addAction: () => history.push(`${location.pathname}/new`),
			}}
			mobileActions={mobileActions}
		/>
	);
};

const OrganizationClientsClientGroupsIndexPage: FC<RouteComponentProps> = (props) => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.client_groups.header.title"));
		setResource(wrapPromise(segmentContext.get(listName, resourceType)));
	}, []);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `${props.match.url}/new`,
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `${props.match.url}/new`,
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("modules.client_groups.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} mobileActions={mobileActions} />
			</Suspense>
		</>
	);
};
export default OrganizationClientsClientGroupsIndexPage;
